import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

// Validation schema
const contactFormSchema = z.object({
  firstName: z.string().trim().min(2, {
    message: "Vorname muss mindestens 2 Zeichen lang sein"
  }).max(100, {
    message: "Vorname darf maximal 100 Zeichen lang sein"
  }),
  lastName: z.string().trim().min(2, {
    message: "Nachname muss mindestens 2 Zeichen lang sein"
  }).max(100, {
    message: "Nachname darf maximal 100 Zeichen lang sein"
  }),
  email: z.string().trim().email({
    message: "Bitte geben Sie eine gültige E-Mail-Adresse ein"
  }).max(255, {
    message: "E-Mail-Adresse darf maximal 255 Zeichen lang sein"
  }),
  company: z.string().trim().max(200, {
    message: "Firmenname darf maximal 200 Zeichen lang sein"
  }).optional(),
  message: z.string().trim().min(10, {
    message: "Nachricht muss mindestens 10 Zeichen lang sein"
  }).max(2000, {
    message: "Nachricht darf maximal 2000 Zeichen lang sein"
  }),
  privacyAccepted: z.boolean().refine((val) => val === true, {
    message: "Bitte akzeptieren Sie die Datenschutzerklärung"
  })
});
type ContactFormValues = z.infer<typeof contactFormSchema>;
export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    toast
  } = useToast();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      message: "",
      privacyAccepted: false
    }
  });
  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabase.functions.invoke('send-contact-email', {
        body: data
      });
      if (error) {
        console.error('Error invoking function:', error);
        toast({
          title: "Fehler beim Versenden",
          description: "Ihre Nachricht konnte nicht versendet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.",
          variant: "destructive"
        });
        return;
      }
      toast({
        title: "Nachricht erfolgreich versendet!",
        description: "Wir haben Ihre Anfrage erhalten und melden uns zeitnah bei Ihnen."
      });
      form.reset();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "Fehler beim Versenden",
        description: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="py-20 sm:py-32 bg-gradient-story relative overflow-hidden" aria-labelledby="contact-heading">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0" aria-hidden="true"></div>
      
      <div className="container mx-auto px-6 sm:px-8 relative z-10 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Refined Contact Info */}
          <div className="order-2 lg:order-1 space-y-8">
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-px bg-gradient-to-r from-primary/60 to-transparent" aria-hidden="true"></div>
                <div className="w-2 h-2 rounded-full bg-primary/40" aria-hidden="true"></div>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-sm tracking-[0.2em] uppercase text-primary/80">
                  Kontakt & Beratung
                </p>
                <h2 id="contact-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-light text-foreground leading-[0.95] sm:leading-[0.85]">
                  Persönliche Beratung für
                  <span className="block text-primary font-medium mt-2 break-words hyphens-auto" style={{
                  hyphens: 'auto'
                }}>außergewöhnliche Positionen</span>
                </h2>
              </div>
            </div>
            
            <div className="space-y-6">
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed font-light max-w-lg">
                In einem vertraulichen Gespräch entwickeln wir gemeinsam die optimale Strategie 
                für Ihre Herausforderungen in der Hospitality.
              </p>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-start space-x-5 group">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-all duration-300 shadow-sm">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <div className="text-foreground font-display font-medium text-lg">Direkter Kontakt</div>
                  <div className="text-muted-foreground text-base">
                    <a href="tel:+4941414263112" className="hover:text-primary transition-colors" aria-label="Anrufen unter +49 4141 4263112">+49 4141 - 426310</a>
                  </div>
                  <div className="text-muted-foreground text-sm">
                    
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-5 group">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-all duration-300 shadow-sm">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <div className="text-foreground font-display font-medium text-lg">E-Mail Kontakt</div>
                  <div className="text-muted-foreground text-base">
                    <a href="mailto:m.scholz@scholz-interim.de" className="hover:text-primary transition-colors" aria-label="E-Mail senden an m.scholz@scholz-interim.de">
                      m.scholz@scholz-interim.de
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-5 group">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-all duration-300 shadow-sm">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <div className="text-foreground font-display font-medium text-lg">Standort</div>
                  <address className="text-muted-foreground text-base not-italic">
                    Römerstraße 6, 21680 Stade
                  </address>
                </div>
              </div>
            </div>
          </div>
          
          {/* Elegant Contact Form */}
          <div className="group relative order-1 lg:order-2">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent blur-3xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" aria-hidden="true"></div>
            
            <Card className="p-8 sm:p-10 lg:p-12 border border-border/40 shadow-[0_20px_70px_rgba(0,0,0,0.15),0_8px_25px_rgba(0,0,0,0.1)] bg-background/70 backdrop-blur-xl relative overflow-hidden hover:scale-[1.02] transition-all duration-500 ease-out">
              {/* Animated gradient border */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/15 via-primary/8 to-transparent pointer-events-none -z-10" aria-hidden="true"></div>
              
              {/* Refined accent overlay */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/3 via-transparent to-primary/3 pointer-events-none" aria-hidden="true"></div>
            
            <div className="relative z-10 space-y-8">
              <div className="space-y-3">
                <h3 className="text-2xl lg:text-3xl font-display font-light text-foreground">
                  Kontakt aufnehmen
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Teilen Sie uns Ihre Anfrage mit – wir melden uns zeitnah zurück.
                </p>
              </div>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField control={form.control} name="firstName" render={({
                      field
                    }) => <FormItem>
                          <FormLabel>Vorname *</FormLabel>
                          <FormControl>
                            <Input placeholder="Ihr Vorname" {...field} required aria-required="true" className="h-12 border-border/50 bg-background/40 focus:bg-background/60 focus:border-primary/30 transition-all duration-300 hover:shadow-md" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name="lastName" render={({
                      field
                    }) => <FormItem>
                          <FormLabel>Nachname *</FormLabel>
                          <FormControl>
                            <Input placeholder="Ihr Nachname" {...field} required aria-required="true" className="h-12 border-border/50 bg-background/40 focus:bg-background/60 focus:border-primary/30 transition-all duration-300 hover:shadow-md" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>
                  
                  <FormField control={form.control} name="email" render={({
                    field
                  }) => <FormItem>
                        <FormLabel>E-Mail Adresse *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="ihre.email@unternehmen.de" {...field} required aria-required="true" className="h-12 border-border/50 bg-background/40 focus:bg-background/60 focus:border-primary/30 transition-all duration-300 hover:shadow-md" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  
                  <FormField control={form.control} name="company" render={({
                    field
                  }) => <FormItem>
                        <FormLabel>Unternehmen</FormLabel>
                        <FormControl>
                          <Input placeholder="Ihr Unternehmen" {...field} className="h-12 border-border/50 bg-background/40 focus:bg-background/60 focus:border-primary/30 transition-all duration-300 hover:shadow-md" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  
                  <FormField control={form.control} name="message" render={({
                    field
                  }) => <FormItem>
                        <FormLabel>Ihre Nachricht *</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Beschreiben Sie kurz Ihre Anfrage und welche Position Sie besetzen möchten..." {...field} required aria-required="true" className="min-h-[120px] border-border/50 bg-background/40 focus:bg-background/60 focus:border-primary/30 transition-all duration-300 resize-none hover:shadow-md" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  
                  <FormField control={form.control} name="privacyAccepted" render={({
                    field
                  }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-normal">
                            Ich habe die{" "}
                            <Link to="/datenschutz" className="text-primary hover:underline">
                              Datenschutzerklärung
                            </Link>{" "}
                            gelesen und akzeptiere diese. *
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>} />
                  
                  <Button type="submit" variant="premium" size="lg" disabled={isSubmitting} className="w-full h-12 font-display font-medium text-base shadow-luxury hover:shadow-story transition-all duration-300 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-1000" aria-label="Anfrage absenden per E-Mail">
                    {isSubmitting ? <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Wird gesendet...
                      </> : 'Anfrage senden'}
                  </Button>
                  
                  <div className="flex items-center justify-center space-x-3 pt-2">
                    <div className="w-1 h-1 rounded-full bg-primary/20" aria-hidden="true"></div>
                    <span className="text-xs text-muted-foreground font-light" role="note">
                      Alle Angaben werden streng vertraulich behandelt
                    </span>
                    <div className="w-1 h-1 rounded-full bg-primary/20" aria-hidden="true"></div>
                  </div>
                </form>
              </Form>
            </div>
          </Card>
          </div>
        </div>
      </div>
    </section>;
};